import { useState, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import BrandIcon from "../components/app/BrandIcon";
import { useTranslation } from "react-i18next";
import api, { extractAxiosError } from "../lib/api";

export default function ResetPasswordPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = useMemo(() => params.get("token") || "", [params]);

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) {
      setError(t("auth.resetMinPwd"));
      return;
    }
    if (password !== confirm) {
      setError(t("auth.resetMismatch"));
      return;
    }
    if (!token) {
      setError(t("auth.resetTokenMissing"));
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      // Push to login with a success flag in state
      navigate("/login?reset=ok", { replace: true });
    } catch (err) {
      setError(extractAxiosError(err, t("auth.resetErrorGeneric")));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="theme-swiss min-h-screen flex items-center justify-center p-6 bg-slate-900">
      <form
        onSubmit={submit}
        className="w-full max-w-md fade-in-up"
        data-testid="reset-password-form"
      >
        <Link to="/" className="flex items-center gap-3 mb-8 group" data-testid="logo-home">
          <BrandIcon size={40} />
          <div className="font-display font-bold text-xl text-slate-100 group-hover:text-orange-300 transition-colors">MachinePilot</div>
        </Link>

        <div className="label-tech mb-2">{t("auth.accessTitle")}</div>
        <h2 className="font-display font-bold text-3xl text-slate-100">{t("auth.resetTitle")}</h2>
        <p className="text-slate-400 mt-2 text-sm">{t("auth.resetSubtitle")}</p>

        <div className="mt-8 space-y-4">
          <div>
            <label className="label-tech block mb-2">{t("auth.newPassword")}</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 rounded-md text-sm"
              required
              minLength={6}
              data-testid="reset-password"
            />
          </div>
          <div>
            <label className="label-tech block mb-2">{t("auth.confirmPassword")}</label>
            <input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="w-full px-4 py-3 rounded-md text-sm"
              required
              minLength={6}
              data-testid="reset-confirm"
            />
          </div>
        </div>

        {error && (
          <div
            className="mt-4 px-4 py-3 rounded-md bg-red-500/10 border border-red-500/40 text-red-300 text-sm"
            data-testid="reset-error"
          >
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={busy}
          className="btn-primary w-full mt-8 px-5 py-3 rounded-md flex items-center justify-center gap-2"
          data-testid="reset-submit"
        >
          {busy ? t("auth.savingPassword") : (<>{t("auth.savePassword")} <ArrowRight size={16} /></>)}
        </button>

        <div className="mt-6 text-sm text-slate-400 text-center">
          <Link to="/login" className="text-blue-400 hover:text-blue-300 font-medium" data-testid="back-to-login">
            {t("auth.backToLogin")}
          </Link>
        </div>
      </form>
    </div>
  );
}
